<template>
  <div class="w-full">
    <div class="video-container">
      <iframe :id="playerElementId" :src="proxyVideoUrl" allowfullscreen></iframe>
    </div>
  </div>
</template>

<script>
// TODO: Need to verify Mixpanel analytics for tracking
import { trackEvent } from '@/utils/mixpanel';

export default {
  name: 'ProxyPlayer',
  emits: ['play'],
  props: {
    id: {
      type: String,
      default: null,
    },
    videoUrl: {
      type: String,
      required: true,
    },
    metadata: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      lastTimeUpdate: 0,
      accumulatedTimeWatched: 0,
      sendInterval: 60, // in seconds
      progressTracked: [], // Keeps track of the progress points already tracked
      isPlaying: false,
      trackingInterval: null,
      hasStarted: false,
      messageListener: null,
    };
  },
  computed: {
    videoId() {
      // Extract YouTube video ID from URL
      const url = this.videoUrl;

      let extractedId = null;
      if (url.includes('youtube.com/watch')) {
        extractedId = new URL(url).searchParams.get('v');
      } else if (url.includes('youtu.be/')) {
        extractedId = url.split('youtu.be/')[1].split('?')[0];
      } else if (
        url.includes('youtube.com/embed/') ||
        url.includes('youtube-nocookie.com/embed/')
      ) {
        // Handle both youtube.com/embed/ and youtube-nocookie.com/embed/
        const embedMatch = url.match(/\/embed\/([^?]+)/);
        extractedId = embedMatch ? embedMatch[1] : null;
      } else if (url.includes('youtube.html')) {
        // Handle proxy URL format
        const urlParams = new URLSearchParams(url.split('?')[1] || '');
        extractedId = urlParams.get('v');
      } else {
        extractedId = url;
      }

      return extractedId;
    },
    playerElementId() {
      // Generate unique ID for the player element
      // Use provided id prop, or fallback to videoId, or generate a unique ID
      if (this.id) {
        return `youtube-player-${this.id}`;
      }
      // Fallback to videoId if available, otherwise use a timestamp-based ID
      const baseId = this.videoId || `player-${Date.now()}`;
      return `youtube-player-${baseId}`;
    },
    videoTitle() {
      return this.metadata?.title || 'YouTube Video';
    },
    appOrigin() {
      return process.env.VUE_APP_APP_DOMAIN || window.location.origin;
    },
    proxyVideoUrl() {
      const videoId = this.videoId;

      if (!videoId) {
        return '';
      }

      // Build URL with video ID and autoplay parameter
      const params = new URLSearchParams({
        v: videoId,
        autoplay: '0',
      });
      const origin = process.env.VUE_APP_APP_DOMAIN || window.location.origin;

      return `${origin}/youtube?${params.toString()}`;
    },
  },
  mounted() {
    // Set up message listener for iframe communication
    this.setupMessageListener();
  },
  beforeUnmount() {
    if (this.trackingInterval) {
      clearInterval(this.trackingInterval);
    }
    // Remove message listener
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }
    // Send any accumulated time before unmounting
    if (this.accumulatedTimeWatched > 0) {
      this.sendAccumulatedTimeWatched();
    }
  },
  methods: {
    setupMessageListener() {
      // Listen for messages from the youtube.html iframe
      // This allows us to track video events if youtube.html sends them
      this.messageListener = event => {
        // Verify origin for security
        if (event.origin !== this.appOrigin) {
          return;
        }

        // Handle messages from youtube.html iframe
        if (event.data && event.data.type === 'youtube-player') {
          const { action, data } = event.data;

          switch (action) {
            case 'play':
              this.onPlay();
              break;
            case 'pause':
              this.onPause();
              break;
            case 'ended':
              this.onEnded();
              break;
            case 'timeupdate':
              if (data && data.currentTime !== undefined && data.duration !== undefined) {
                this.handleTimeUpdate(data.currentTime, data.duration);
              }
              break;
            default:
              break;
          }
        }
      };

      window.addEventListener('message', this.messageListener);
    },
    handleTimeUpdate(currentTime, totalDuration) {
      if (!totalDuration) return;

      // Calculate time watched
      let timeWatched = currentTime - this.lastTimeUpdate;
      if (timeWatched < 0) {
        timeWatched = 0;
      }
      this.accumulatedTimeWatched += timeWatched;

      // Track progress
      const progressPoints = [0.1, 0.25, 0.5, 0.75, 0.9, 1];
      for (const point of progressPoints) {
        if (currentTime >= point * totalDuration && !this.progressTracked.includes(point)) {
          this.trackVideoProgress(point);
          this.progressTracked.push(point);
        }
      }

      // Send event to Google Analytics 4 every 60 seconds
      if (this.accumulatedTimeWatched >= this.sendInterval) {
        this.sendAccumulatedTimeWatched();
      }

      this.lastTimeUpdate = currentTime;
    },
    onPlay() {
      if (!this.hasStarted) {
        this.trackVideoStart();
        this.hasStarted = true;
      }

      this.isPlaying = true;
      // Mirrors VideoPlayer's `play`, so a host can react to playback starting
      // without caring which branch it mounted (the lesson resource card hides its
      // duration badge on it). NOTE: on a native build this does not fire yet —
      // the iframe posts to `VUE_APP_APP_DOMAIN` while the parent is
      // `capacitor://localhost` / `http://localhost`, so the browser drops every
      // message from it. Fixing that targetOrigin also restores this player's
      // Mixpanel/GA video events on device, which are dead for the same reason.
      this.$emit('play');
    },
    onPause() {
      this.isPlaying = false;
    },
    onEnded() {
      this.isPlaying = false;
      this.trackVideoEnd();
    },
    trackVideoProgress(progress) {
      const progressPercentage = progress * 100;

      // Track to Google Analytics
      if (window.gtag) {
        window.gtag('event', 'video_progress', {
          video_title: this.videoTitle,
          progress_percentage: progressPercentage,
          video_type: 'youtube',
        });
      }

      // Track to Mixpanel
      trackEvent('Video Progress', {
        video_title: this.videoTitle,
        video_url: this.videoUrl,
        progress_percentage: progressPercentage,
        progress_point: progress,
        video_id: this.id || this.videoId,
        video_type: 'youtube',
      });
    },
    trackVideoStart() {
      // Reset progress tracking when video starts over
      this.progressTracked = [];

      // Track to Google Analytics
      if (window.gtag) {
        window.gtag('event', 'video_start', {
          video_title: this.videoTitle,
          video_type: 'youtube',
        });
      }

      // Track to Mixpanel
      trackEvent('Video Start', {
        video_title: this.videoTitle,
        video_url: this.videoUrl,
        video_id: this.id || this.videoId,
        video_type: 'youtube',
      });
    },
    trackVideoEnd() {
      this.sendAccumulatedTimeWatched();

      // Track to Google Analytics
      if (window.gtag) {
        window.gtag('event', 'video_end', {
          video_title: this.videoTitle,
          video_type: 'youtube',
        });
      }

      // Track to Mixpanel
      trackEvent('Video End', {
        video_title: this.videoTitle,
        video_url: this.videoUrl,
        video_id: this.id || this.videoId,
        total_time_watched: this.accumulatedTimeWatched,
        video_type: 'youtube',
      });
    },
    sendAccumulatedTimeWatched() {
      if (window.gtag) {
        window.gtag('event', 'video_watch_duration', {
          video_title: this.videoTitle,
          time_watched: this.accumulatedTimeWatched,
          video_type: 'youtube',
        });
      }
      this.accumulatedTimeWatched = 0;
    },
    // Expose methods similar to VideoPlayer for consistency
    // Note: These methods require postMessage communication with youtube.html iframe
    seek(seconds) {
      const iframe = document.getElementById(this.playerElementId);
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: 'youtube-control',
            action: 'seek',
            seconds: seconds,
          },
          window.location.origin,
        );
      }
    },
    play() {
      const iframe = document.getElementById(this.playerElementId);
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: 'youtube-control',
            action: 'play',
          },
          window.location.origin,
        );
      }
    },
  },
};
</script>

<style scoped>
.video-container {
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 aspect ratio (9/16 = 0.5625) */
  overflow: hidden;
}

.video-container > div {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* Target iframe directly */
.video-container iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}
</style>
